import { useEffect, useState } from 'react';
import type { PaymentTerminal } from '@equinox/shared';
import { useI18n } from '../../i18n/I18nContext.js';
import { paymentTerminalsApi } from '../../api/resources.js';
import { ApiError } from '../../lib/apiClient.js';
import { Banner } from '../../components/Banner.js';
import { Modal } from '../../components/Modal.js';
import { ConfirmDialog } from '../../components/ConfirmDialog.js';

type Processor = 'mercadopago' | 'clip';

function PaymentTerminalFormModal({
  terminal,
  onClose,
  onSaved,
}: {
  terminal: PaymentTerminal | null;
  onClose(): void;
  onSaved(t: PaymentTerminal): void;
}) {
  const { t } = useI18n();
  const [processor, setProcessor] = useState<Processor>(terminal?.processor ?? 'mercadopago');
  const [externalTerminalId, setExternalTerminalId] = useState(terminal?.externalTerminalId ?? '');
  const [displayName, setDisplayName] = useState(terminal?.displayName ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const canSave = externalTerminalId.trim() && displayName.trim() && !saving;

  async function submit() {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    try {
      const saved = terminal
        ? await paymentTerminalsApi.update(terminal.id, {
            processor,
            externalTerminalId: externalTerminalId.trim(),
            displayName: displayName.trim(),
          })
        : await paymentTerminalsApi.create({
            processor,
            externalTerminalId: externalTerminalId.trim(),
            displayName: displayName.trim(),
          });
      onSaved(saved);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.error'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal title={terminal ? t('common.edit') : t('paymentTerminals.new')} onClose={onClose}>
      {error && <p className="field-error">{error}</p>}
      <div className="field">
        <label htmlFor="payment-terminal-processor">{t('paymentTerminals.processor')}</label>
        <select
          id="payment-terminal-processor"
          value={processor}
          onChange={(e) => setProcessor(e.target.value as Processor)}
          disabled={saving}
        >
          <option value="mercadopago">{t('paymentTerminals.processorMercadoPago')}</option>
          <option value="clip">{t('paymentTerminals.processorClip')}</option>
        </select>
      </div>
      <div className="field">
        <label htmlFor="payment-terminal-external-id">{t('paymentTerminals.externalTerminalId')}</label>
        <input
          id="payment-terminal-external-id"
          value={externalTerminalId}
          onChange={(e) => setExternalTerminalId(e.target.value)}
          disabled={saving}
          required
        />
        <span className="field-hint">
          {processor === 'mercadopago'
            ? t('paymentTerminals.externalTerminalIdHintMercadoPago')
            : t('paymentTerminals.externalTerminalIdHintClip')}
        </span>
      </div>
      <label htmlFor="payment-terminal-display-name">
        {t('paymentTerminals.displayName')}
        <input
          id="payment-terminal-display-name"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          disabled={saving}
          required
        />
      </label>
      <span className="field-hint">{t('paymentTerminals.displayNameHint')}</span>
      <div className="modal__actions">
        <button type="button" className="btn btn--secondary" onClick={onClose} disabled={saving}>
          {t('common.cancel')}
        </button>
        <button type="button" className="btn btn--primary" disabled={!canSave} onClick={() => void submit()}>
          {t('paymentTerminals.save')}
        </button>
      </div>
    </Modal>
  );
}

export function PaymentTerminalsPage() {
  const { t } = useI18n();
  const [terminals, setTerminals] = useState<PaymentTerminal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  /**
   * Separate from `error` (found in PR review): `error` is shown in the
   * banner for ANY failure, list load or action, but only a failed LOAD
   * should hide the table/empty-state — a failed deactivate/reactivate
   * must leave the table (and the row the operator was just looking at)
   * exactly where it was, with the banner the only thing that changes.
   */
  const [loadError, setLoadError] = useState<string | null>(null);
  const [editing, setEditing] = useState<PaymentTerminal | 'new' | null>(null);
  const [deactivateTarget, setDeactivateTarget] = useState<PaymentTerminal | null>(null);

  async function reload() {
    setLoading(true);
    setError(null);
    setLoadError(null);
    try {
      setTerminals(await paymentTerminalsApi.list(true));
    } catch (err) {
      const message = err instanceof ApiError ? err.message : t('common.error');
      setError(message);
      setLoadError(message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void reload();
  }, []);

  function processorLabel(processor: Processor): string {
    return processor === 'mercadopago' ? t('paymentTerminals.processorMercadoPago') : t('paymentTerminals.processorClip');
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
      <div className="page-header">
        <div>
          <h1>{t('paymentTerminals.title')}</h1>
          <p>{t('paymentTerminals.subtitle')}</p>
        </div>
        <button type="button" className="btn btn--primary" onClick={() => setEditing('new')}>
          {t('paymentTerminals.new')}
        </button>
      </div>

      {error && <Banner kind="danger">{error}</Banner>}

      {loading ? (
        <p>{t('common.loading')}</p>
      ) : loadError ? null : terminals.length === 0 ? (
        <p>{t('paymentTerminals.empty')}</p>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>{t('paymentTerminals.processor')}</th>
                <th>{t('paymentTerminals.externalTerminalId')}</th>
                <th>{t('paymentTerminals.displayName')}</th>
                <th>{t('common.status')}</th>
                <th>{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {terminals.map((pt) => (
                <tr key={pt.id}>
                  <td>{processorLabel(pt.processor)}</td>
                  <td>{pt.externalTerminalId}</td>
                  <td>{pt.displayName}</td>
                  <td>
                    <span className={`badge badge--${pt.active ? 'ok' : 'neutral'}`}>
                      {pt.active ? t('common.active') : t('common.inactive')}
                    </span>
                  </td>
                  <td style={{ display: 'flex', gap: 'var(--space-2)' }}>
                    <button type="button" className="btn btn--ghost btn--sm" onClick={() => setEditing(pt)}>
                      {t('common.edit')}
                    </button>
                    <button type="button" className="btn btn--ghost btn--sm" onClick={() => setDeactivateTarget(pt)}>
                      {pt.active ? t('paymentTerminals.deactivate') : t('paymentTerminals.activate')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <PaymentTerminalFormModal
          terminal={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(saved) => {
            setTerminals((prev) => {
              const exists = prev.some((pt) => pt.id === saved.id);
              return exists ? prev.map((pt) => (pt.id === saved.id ? saved : pt)) : [...prev, saved];
            });
            setEditing(null);
          }}
        />
      )}

      {deactivateTarget && (
        <ConfirmDialog
          title={deactivateTarget.active ? t('paymentTerminals.deactivate') : t('paymentTerminals.activate')}
          consequence={
            deactivateTarget.active
              ? t('paymentTerminals.deactivateConfirm')
              : t('paymentTerminals.activateConfirm')
          }
          danger={deactivateTarget.active}
          onCancel={() => setDeactivateTarget(null)}
          onConfirm={async () => {
            const target = deactivateTarget;
            setDeactivateTarget(null);
            try {
              const updated = target.active
                ? await paymentTerminalsApi.deactivate(target.id)
                : await paymentTerminalsApi.activate(target.id);
              setTerminals((prev) => prev.map((pt) => (pt.id === updated.id ? updated : pt)));
            } catch (err) {
              setError(err instanceof ApiError ? err.message : t('common.error'));
            }
          }}
        />
      )}
    </div>
  );
}
